'use client'

import PageDesignWrapper from '@/components/page-design-wrapper/page-design-wrapper'
import FeaturedProfilesInCampaign from './featuredProfilesInCampaign'
import FlowbiteIconComponent from '../flowbite-icon/flowbiteIcon'
import { IProfileLink } from '@/lib/homePageFunctions'
import '@/styles/scrollbar.css'
import { useEmblaCarouselResponsive } from '@/utils/react-hooks/useEmblaCarousel'
import { useEffect, useRef, useState } from 'react'
import { IBackgroundColor } from './ProfilesSection'

interface IProps {
  campaignsData: IFeaturedCampaignsData[]
  onSelectedIndexChange?: (index: number) => void
  backgroundColor?: IBackgroundColor
}
export interface IFeaturedCampaignsData {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any
  profileList?: IProfileLink[]
}

export const FeaturedCampaigns = ({ campaignsData, onSelectedIndexChange, backgroundColor }: IProps) => {
  const { emblaRef, canScrollPrev, canScrollNext, selectedIndex, scrollPrev, scrollNext } =
    useEmblaCarouselResponsive({
      options: { loop: false, align: 'start', watchDrag: true },
      breakpoints: [
        {
          query: '(min-width: 1024px)',
          options: { loop: false, align: 'start', watchDrag: false },
        },
      ],
    })
  const [indicatorWidth, setIndicatorWidth] = useState(0)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const indicatorLeft = selectedIndex * indicatorWidth

  useEffect(() => {
    onSelectedIndexChange?.(selectedIndex)
  }, [selectedIndex, onSelectedIndexChange])

  useEffect(() => {
    if (wrapperRef.current) {
      wrapperRef.current.style.setProperty('--campaign-background', backgroundColor?.backgroundColor || '')
      wrapperRef.current.style.setProperty('--campaign-background-dark', backgroundColor?.darkBackgroundColor || '')
    }
  }, [backgroundColor, wrapperRef])

  useEffect(() => {
    const updateWidth = () => {
      const carouselEl = document.getElementById('campaigns-carousel')
      if (carouselEl) {
        setIndicatorWidth(carouselEl.clientWidth / campaignsData.length)
      }
    }
    updateWidth()
    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [campaignsData.length])

  return (
    <div
      ref={wrapperRef}
      className="md:p-4 flex justify-center items-center ease-in duration-500
      bg-gradient-to-t from-[--campaign-background] via-[--campaign-background-dark] to-black [transition-property:_--campaign-background,_--campaign-background-dark]"
    >
      <div className="PageInternalContainer 2xl:w-[70vw] sm:w-[80vw] w-[100vw] flex justify-center items-center">
        {/* Left Button */}
        <div
          className={`mr-[35px] z-10 transition-opacity duration-300 ease-in-out hidden lg:block ${
            canScrollPrev ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <button
            onClick={scrollPrev}
            disabled={!canScrollPrev}
            className="h-[35px] w-[35px] rounded-full bg-[#3C3C50] border border-white text-white shadow-lg hover:bg-[#4b4b66] transition-colors flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <FlowbiteIconComponent
              iconType="AngleDown"
              className="rotate-90 text-white !ml-0"
              iconClassName="w-6 h-6"
            />
          </button>
        </div>

        {/* Carousel */}
        <div className="w-full xl:pt-[136px] pt-3 lg:p-0 p-4">
          <div
            className="h-[5px] relative bg-cm-red rounded-lg transition-all duration-500 ease-in-out"
            style={{ left: `${indicatorLeft}px`, width: `${indicatorWidth}px` }}
          />
          <div id="campaigns-carousel" className="overflow-hidden " ref={emblaRef}>
            <div className="flex">
              {campaignsData.map((campaign, index) => (
                <div key={`${campaign.data?.title}-${index}`} className="w-full flex-shrink-0">
                  <PageDesignWrapper data={campaign.data} />
                  <div className="flex flex-col justify-center items-center">
                    <FeaturedProfilesInCampaign profileList={campaign.profileList} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Button */}
        <div
          className={`ml-[35px] z-10 transition-opacity duration-300 ease-in-out hidden lg:block ${
            canScrollNext ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <button
            onClick={scrollNext}
            disabled={!canScrollNext}
            className="h-[35px] w-[35px] rounded-full bg-[#3C3C50] border border-white text-white shadow-lg hover:bg-[#4b4b66] transition-colors flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <FlowbiteIconComponent
              iconType="AngleDown"
              className="-rotate-90 text-white !ml-0"
              iconClassName="w-6 h-6"
            />
          </button>
        </div>
      </div>
    </div>
  )
}

export default FeaturedCampaigns
